/* eslint-disable node/prefer-global/process */
import type { CountersSuccess } from '@autobid/strapi-integration/typescript/auction/noFilterCounters'
import type { UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useNoFilterCountersSocket } from '@autobid/ui/composables/car/sockets/useNoFilterCountersSocket'
import { useAuctionId } from '@autobid/ui/composables/useAuctionId'
import { prepareMultiErrorCause } from '@autobid/ui/utils/auctions/prepareMultiErrorCause'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted } from 'vue'

// #region contants
const CACHE_TIME = 1000 * 60 * 60
// #endregion

// #region types
type DataType = 'auction' | 'searchList'
// #endregion

export const noFilterCountersQueryKey = {
  all: () => ['no-filter-counters'] as const,
  byType: (type: 'auction' | 'searchList') => ['no-filter-counters', type] as const,
  byAuctionId: (auctionId: number) => ['no-filter-counters', 'auction', auctionId] as const,
  byCarsIds: (carsIds: number[]) => ['no-filter-counters', 'searchList', carsIds] as const,
}

export function useNoFilterCounters(type: DataType, options?: UseQueryOptions<CountersSuccess['data']['items']['metadata'] | null, Error>) {
  const { locale } = useI18n()
  const { $trpc } = useNuxtApp()
  const { init: initSockets } = useNoFilterCountersSocket()
  const auctionId = useAuctionId()
  const searchCarIds = useState('searchCarIds') as Ref<number[]>
  const queryKey = computed(() => {
    if (type === 'auction') {
      return auctionId.value ? noFilterCountersQueryKey.byAuctionId(auctionId.value) : noFilterCountersQueryKey.byType(type)
    }

    return noFilterCountersQueryKey.byCarsIds(searchCarIds.value)
  })

  const queryFn = async (): Promise<
    CountersSuccess['data']['items']['metadata']
  > => {
    const result = await (async function () {
      if (type === 'searchList') {
        return await $trpc.auctions.getNoFilterCounters.mutate({ type: 'search', carIds: searchCarIds.value, lang: locale.value })
      }

      if (type === 'auction' && auctionId.value) {
        return await $trpc.auctions.getNoFilterCounters.mutate({ type: 'auction', auctionId: auctionId.value, lang: locale.value })
      }

      return null
    }())

    if (!result) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if ('errors' in result) {
      throw createError({
        statusCode: result.errors[0].status_code,
        statusMessage: result.errors[0].message,
        cause: prepareMultiErrorCause(result.errors),
      })
    }

    if (!result.data.items?.metadata) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No data',
      })
    }

    return result.data.items.metadata
  }

  onMounted(initSockets)

  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await queryFn()
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
